import { AssessmentReport } from '@entities/module';
import { privateApi } from '@shared/config/axios.config';

import { Answer, AttemptPrice, Question, QuestionType, Quiz } from '../model/types';

interface GetQuizParams {
  id: string;
}

interface QuizDTO {
  id: string;
  module_id: string;
  title: string;
  order: number;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  passed: boolean;
  failed: boolean;
  questions: QuestionDTO[];
  attempt_number: number;
  attempt_price?: AttemptPrice | null;
}

interface QuestionDTO {
  id: string;
  quiz_id: string;
  type: QuestionType;
  text: string;
  order: number;
  answers: AnswerDTO[] | null;
}

interface AnswerDTO {
  id: string;
  question_id: string;
  text: string;
}

export const getQuizApi = (params: GetQuizParams) => {
  return privateApi.get<QuizDTO>(`/account/content/quiz/${params.id}`).then(
    ({ data }) =>
      ({
        id: data.id,
        moduleId: data.module_id,
        title: data.title,
        order: data.order,
        completed: data.passed,
        failed: data.failed,
        isDeleted: data.is_deleted,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        questions: data.questions
          .map(
            (dto) =>
              ({
                id: dto.id,
                quizId: dto.quiz_id,
                type: dto.type,
                text: dto.text,
                order: dto.order,
                answers:
                  dto.answers?.map(
                    (a) =>
                      ({
                        id: a.id,
                        questionId: a.question_id,
                        text: a.text,
                      }) as Answer,
                  ) || [],
              }) as Question,
          )
          .sort((a, b) => a.order - b.order),
        attemptNumber: data.attempt_number,
        attemptPrice: data.attempt_price || undefined,
      }) as Quiz,
  );
};

interface PassQuizPayload {
  quiz_id: string;
  attempt_number: number;
  answers: AnswerPayload[];
}

export interface AnswerPayload {
  question_id: string;
  selected_option_id: string | null;
  answer_text: string | null;
}

export interface AnswerResultDTO {
  id: string;
  attempt_id: string;
  question_id: string;
  selected_option_id: string;
  correct_option_id: string | null;
  question_text: string;
  answer_text: string;
  is_correct: boolean;
}

interface AttemptDTO {
  id: string;
  account_id: string;
  quiz_id: string;
  created_at: string;
  updated_at: string;
  score: number | null;
  answers: AnswerResultDTO[];
}

interface PassQuizResponse {
  attempt: AttemptDTO;
  assessment_passed: boolean;
  passed: boolean;
  failed: boolean;
  progress: {
    account_id: string;
    module_id: string;
    quiz_id: string;
    is_passed: boolean;
    attempt_id: string;
    created_at: string;
  } | null;
  assessment_report?: AssessmentReport;
}

export const passQuizApi = (payload: PassQuizPayload) => {
  return privateApi.post<PassQuizResponse>('/account/quiz/attempt', payload);
};

interface GetQuizAttemptParams {
  quizId: string;
}

export const getQuizAttemptApi = (params: GetQuizAttemptParams) => {
  return privateApi.get<AttemptDTO[] | null>(`/account/quiz/attempt/${params.quizId}`);
};
